<template>
  <v-card color="#111827">
    <v-toolbar dark color="secondary">
      <v-btn icon dark @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
      <v-toolbar-title>Daueraufträge verwalten</v-toolbar-title>
    </v-toolbar>

    <v-card-text class="pt-4">
      <v-data-table
        :headers="headers"
        :items="recurringExpenses"
        :loading="loading"
        loading-text="Lade Daueraufträge..."
        no-data-text="Keine Daueraufträge gefunden."
        dark
        class="elevation-0 transparent">
        <template v-slot:item.studio="{ item }">
          {{ item.studio ? item.studio.name : "N/A" }}
        </template>

        <template v-slot:item.amount="{ item }">
          {{ formatCurrency(item.amount) }}
        </template>

        <template v-slot:item.recurrence.interval="{ item }">
          <span class="text-capitalize">{{ formatInterval(item.recurrence.interval) }}</span>
        </template>

        <template v-slot:item.recurrence.startDate="{ item }">
          {{ formatDate(item.recurrence.startDate) }}
        </template>

        <template v-slot:item.endCondition="{ item }">
          {{ formatEndCondition(item.recurrence) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-icon small class="mr-2" @click="editItem(item)">mdi-pencil</v-icon>
          <v-icon small color="red lighten-1" @click="deleteItem(item)">mdi-delete</v-icon>
        </template>
      </v-data-table>
    </v-card-text>

    <v-dialog v-model="editDialog" max-width="700px" persistent>
      <ExpenseCaptureForm
        v-if="editDialog"
        :studios="studios"
        :invoice-issuers="invoiceIssuers"
        :initial-data="editedItem"
        @close="editDialog = false"
        @expense-saved="onExpenseSaved" />
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="500px">
      <v-card v-if="itemToDelete" color="#1F2937">
        <v-card-title class="headline white--text">Dauerauftrag löschen?</v-card-title>
        <v-card-text class="white--text">
          Bist du sicher, dass du den Dauerauftrag "{{ itemToDelete.title }}" löschen möchtest?
          <v-alert type="warning" dense text class="mt-4">
            <b>Achtung:</b> Diese Aktion ist rückwirkend für die Statistik. Der Dauerauftrag wird aus zukünftigen Berechnungen vollständig entfernt.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false" class="white--text">Abbrechen</v-btn>
          <v-btn color="red darken-1" text @click="confirmDelete" :loading="deleting">Ja, löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import api from "@/api"
import ExpenseCaptureForm from "./ExpenseCaptureForm.vue" // Pfad ggf. anpassen

export default {
  name: "RecurringExpenseManager",
  components: { ExpenseCaptureForm },
  props: {
    studios: { type: Array, default: () => [] },
    invoiceIssuers: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      deleting: false,
      recurringExpenses: [], // Hier ist die fehlende Variable
      editDialog: false,
      deleteDialog: false,
      editedItem: {},
      itemToDelete: {},
      headers: [
        { text: "Titel", value: "title", width: "25%" },
        { text: "Studio", value: "studio" },
        { text: "Betrag", value: "amount", align: "end" },
        { text: "Intervall", value: "recurrence.interval" },
        { text: "Startet am", value: "recurrence.startDate" },
        { text: "Endet", value: "endCondition" },
        { text: "Aktionen", value: "actions", sortable: false, align: "end" },
      ],
    }
  },
  async created() {
    await this.fetchRecurringExpenses()
  },
  methods: {
    async fetchRecurringExpenses() {
      this.loading = true
      try {
        this.recurringExpenses = await api.expenses.fetchAllRecurring()
      } catch (error) {
        this.$toast.error("Daueraufträge konnten nicht geladen werden.")
      } finally {
        this.loading = false
      }
    },
    formatDate(date) {
      return date ? new Date(date).toLocaleDateString("de-CH") : "-"
    },
    formatCurrency(value) {
      if (typeof value !== "number") return "-"
      return value.toLocaleString("de-CH", { style: "currency", currency: "CHF" })
    },
    formatInterval(interval) {
      const map = { weekly: "Wöchentlich", monthly: "Monatlich", quarterly: "Quartalsweise", yearly: "Jährlich" }
      return map[interval] || interval
    },
    formatEndCondition(recurrence) {
      if (!recurrence) return "-"
      if (recurrence.endDate) return `am ${this.formatDate(recurrence.endDate)}`
      if (recurrence.occurrences) return `nach ${recurrence.occurrences} Mal`
      return "Unbegrenzt"
    },
    editItem(item) {
      this.editedItem = JSON.parse(JSON.stringify(item)) // Tiefe Kopie erstellen
      this.editDialog = true
    },
    deleteItem(item) {
      this.itemToDelete = item
      this.deleteDialog = true
    },
    async confirmDelete() {
      this.deleting = true
      try {
        await api.expenses.delete(this.itemToDelete._id)
        this.$toast.success("Dauerauftrag gelöscht.")
        this.deleteDialog = false
        await this.fetchRecurringExpenses() // Liste neu laden
      } catch (error) {
        this.$toast.error("Fehler beim Löschen.")
      } finally {
        this.deleting = false
      }
    },
    async onExpenseSaved() {
      this.editDialog = false
      this.$toast.success("Dauerauftrag erfolgreich gespeichert.")
      await this.fetchRecurringExpenses() // Liste nach dem Speichern neu laden
    },
  },
}
</script>
